import classNames from "classnames"
import React, {
    Dispatch,
    SetStateAction,
    RefObject,
    ReactNode,
    useCallback,
    useState,
    useEffect,
} from "react"
import Draggable from "react-draggable"

const globalCursorOverrideClass = "override-global-cursor-grab" as const

export const AutoHeightRnD = ({
    position,
    setPosition,
    width,
    minWidth,
    setWidth,
    contentRef,
    children,
    className,
    dragHandleClassName,
    cancel,
}: {
    position: { x: number; y: number }
    setPosition: Dispatch<SetStateAction<{ x: number; y: number }>>
    width: number
    minWidth: number
    setWidth: Dispatch<SetStateAction<number>>
    contentRef: RefObject<HTMLDivElement | null>
    children?: ReactNode
    className?: string
    dragHandleClassName?: string
    cancel?: string
}) => {
    const [isDragging, setIsDragging] = useState(false)
    useEffect(() => {
        if (isDragging) {
            document.body.classList.add(globalCursorOverrideClass)
        } else {
            document.body.classList.remove(globalCursorOverrideClass)
        }
        return () => {
            document.body.classList.remove(globalCursorOverrideClass)
        }
    }, [isDragging])
    const getResizeHelperValues = useCallback(() => {
        const startWidth = width
        const startHeight = contentRef.current!.getBoundingClientRect().height
        const aspectRatio = startWidth / startHeight
        const parent = contentRef.current?.offsetParent as HTMLElement
        const maxWidth = parent.clientWidth || Infinity
        const maxHeight = parent.clientHeight || Infinity
        return { startWidth, startHeight, aspectRatio, maxWidth, maxHeight }
    }, [contentRef, width])
    const safeSetState = useCallback(
        ({
            newWidth,
            newHeight,
            maxWidth,
            maxHeight,
            newPosition,
        }: {
            newWidth: number
            newHeight: number
            maxWidth: number
            maxHeight: number
            newPosition?: { x: number; y: number }
        }) => {
            if (newWidth > minWidth && newWidth <= maxWidth && newHeight <= maxHeight) {
                setWidth(newWidth)
                if (newPosition) {
                    setPosition(newPosition)
                }
            }
        },
        [minWidth, setPosition, setWidth]
    )
    return (
        <Draggable
            bounds="parent"
            position={position}
            onStart={() => setIsDragging(true)}
            onStop={(_, d) => {
                setIsDragging(false)
                setPosition({
                    x: d.x,
                    y: d.y,
                })
            }}
            nodeRef={contentRef}
            handle={dragHandleClassName}
            cancel={cancel}
        >
            <div
                className={classNames(
                    "p-2 bg-gray-800 rounded-xl !pointer-events-auto relative",
                    className
                )}
                style={{ width }}
                ref={contentRef}
            >
                {children}
                <div
                    // right
                    className="absolute top-0 right-0 bottom-0 w-2 cursor-ew-resize"
                    draggable={false}
                    onDragStart={(e) => {
                        e.preventDefault()
                    }}
                    onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        const startX = e.clientX
                        const { startWidth, aspectRatio, maxWidth, maxHeight } =
                            getResizeHelperValues()
                        const onMouseMove = (e: MouseEvent) => {
                            const delta = e.clientX - startX
                            const newWidth = startWidth + delta
                            const newHeight = newWidth / aspectRatio
                            safeSetState({ newWidth, newHeight, maxWidth, maxHeight })
                        }
                        const onMouseUp = () => {
                            document.removeEventListener("mousemove", onMouseMove)
                            document.removeEventListener("mouseup", onMouseUp)
                        }
                        document.addEventListener("mousemove", onMouseMove)
                        document.addEventListener("mouseup", onMouseUp)
                    }}
                />
                <div
                    // left
                    className="absolute top-0 left-0 bottom-0 w-2 cursor-ew-resize"
                    draggable={false}
                    onDragStart={(e) => {
                        e.preventDefault()
                    }}
                    onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        const startX = e.clientX
                        const startPositionX = position.x
                        const { startWidth, aspectRatio, maxWidth, maxHeight } =
                            getResizeHelperValues()
                        const onMouseMove = (e: MouseEvent) => {
                            const delta = e.clientX - startX
                            const newWidth = startWidth - delta
                            const newHeight = newWidth / aspectRatio
                            safeSetState({
                                newWidth,
                                newHeight,
                                maxWidth,
                                maxHeight,
                                newPosition: { x: startPositionX + delta, y: position.y },
                            })
                        }
                        const onMouseUp = () => {
                            document.removeEventListener("mousemove", onMouseMove)
                            document.removeEventListener("mouseup", onMouseUp)
                        }
                        document.addEventListener("mousemove", onMouseMove)
                        document.addEventListener("mouseup", onMouseUp)
                    }}
                />
                <div
                    // bottom
                    className="absolute left-0 right-0 bottom-0 h-2 cursor-ns-resize"
                    draggable={false}
                    onDragStart={(e) => {
                        e.preventDefault()
                    }}
                    onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        const startY = e.clientY
                        const { startHeight, aspectRatio, maxWidth, maxHeight } =
                            getResizeHelperValues()
                        const onMouseMove = (e: MouseEvent) => {
                            const delta = e.clientY - startY
                            const newHeight = startHeight + delta
                            const newWidth = newHeight * aspectRatio
                            safeSetState({ newWidth, newHeight, maxWidth, maxHeight })
                        }
                        const onMouseUp = () => {
                            document.removeEventListener("mousemove", onMouseMove)
                            document.removeEventListener("mouseup", onMouseUp)
                        }
                        document.addEventListener("mousemove", onMouseMove)
                        document.addEventListener("mouseup", onMouseUp)
                    }}
                />
                <div
                    // top
                    className="absolute left-0 right-0 top-0 h-2 cursor-ns-resize"
                    draggable={false}
                    onDragStart={(e) => {
                        e.preventDefault()
                    }}
                    onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        const startY = e.clientY
                        const startPositionY = position.y
                        const { startHeight, aspectRatio, maxWidth, maxHeight } =
                            getResizeHelperValues()
                        const onMouseMove = (e: MouseEvent) => {
                            const delta = e.clientY - startY
                            const newHeight = startHeight - delta
                            const newWidth = newHeight * aspectRatio
                            safeSetState({
                                newWidth,
                                newHeight,
                                maxWidth,
                                maxHeight,
                                newPosition: { x: position.x, y: startPositionY + delta },
                            })
                        }
                        const onMouseUp = () => {
                            document.removeEventListener("mousemove", onMouseMove)
                            document.removeEventListener("mouseup", onMouseUp)
                        }
                        document.addEventListener("mousemove", onMouseMove)
                        document.addEventListener("mouseup", onMouseUp)
                    }}
                />
            </div>
        </Draggable>
    )
}
